import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';

interface User {
  email: string;
  firstName?: string;
  lastName?: string;
}

interface AuthContextType {
  isLoggedIn: boolean;
  user: User | null;
  accessGranted: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  grantAccess: (userData?: User, isTemporary?: boolean) => void;
  checkAccess: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => {
    const stored = localStorage.getItem('bargad_isLoggedIn');
    return stored === 'true';
  });

  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem('bargad_user');
    return stored ? JSON.parse(stored) : null;
  });

  const [accessGranted, setAccessGranted] = useState<boolean>(() => {
    const stored = localStorage.getItem('bargad_accessGranted');
    return stored === 'true';
  });

  // Debounce localStorage writes to reduce blocking - 100ms delay
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      localStorage.setItem('bargad_isLoggedIn', isLoggedIn.toString());
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [isLoggedIn]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (user) {
        localStorage.setItem('bargad_user', JSON.stringify(user));
      } else {
        localStorage.removeItem('bargad_user');
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [user]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      localStorage.setItem('bargad_accessGranted', accessGranted.toString());
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [accessGranted]);

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const authorizedEmail = 'manish.kalantre@bargad.ai';
        const authorizedPassword = 'Bargad.manish@2025';
        
        if (email === authorizedEmail && password === authorizedPassword) {
          setIsLoggedIn(true);
          setUser({ email });
          setAccessGranted(true); // Grant access for authorized user
          resolve(true);
        } else if (email && password) {
          // Wrong credentials - do not log in
          resolve(false);
        } else {
          resolve(false);
        }
      }, 500);
    });
  }, []);

  const logout = useCallback(() => {
    setIsLoggedIn(false);
    setUser(null);
    setAccessGranted(false);
    localStorage.removeItem('bargad_isLoggedIn');
    localStorage.removeItem('bargad_user');
    localStorage.removeItem('bargad_accessGranted');
  }, []);

  const grantAccess = useCallback((userData?: User, isTemporary: boolean = false) => {
    console.log('grantAccess called - granting access now', { isTemporary });
    
    // Always grant access - works every time regardless of current state
    // Set states immediately - React will batch these updates
    setIsLoggedIn(true);
    if (userData) {
      setUser(userData);
    }
    // Always set accessGranted to true
    setAccessGranted(true);
    
    // Only persist to localStorage if it's NOT a temporary unlock
    // Temporary unlocks are for development and should not persist across reloads
    if (!isTemporary) {
      localStorage.setItem('bargad_isLoggedIn', 'true');
      localStorage.setItem('bargad_accessGranted', 'true');
      if (userData) {
        localStorage.setItem('bargad_user', JSON.stringify(userData));
      }
      console.log('grantAccess - Access persisted to localStorage');
    } else {
      console.log('grantAccess - Temporary access (not persisted to localStorage)');
    }
    
    console.log('grantAccess completed - access should be granted');
  }, []);

  const checkAccess = useCallback((): boolean => {
    return isLoggedIn && accessGranted;
  }, [isLoggedIn, accessGranted]);

  // Memoize context value to prevent unnecessary re-renders, but ensure it updates when state changes
  const contextValue = useMemo(
    () => ({
      isLoggedIn,
      user,
      accessGranted,
      login,
      logout,
      grantAccess,
      checkAccess,
    }),
    [isLoggedIn, user, accessGranted, login, logout, grantAccess, checkAccess]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

